import { useEffect, useMemo } from "react";
import Loading from "../../../components/utils/Loading";
import { useTranslation } from "react-i18next";
import WasteHistogram from "./StatisticsMonthlyDistribution";
import { capitalizeFirstLetter } from "../../../components/utils/Date";
import { useQualityStatisticsData } from "../hooks/useQualityStatisticsData";
import { useQualityChartSelections } from "../hooks/useQualityChartSelections";
import {
  extractFluxData,
  extractCharacterizationData,
  buildSortingErrorByFluxChartData,
} from "../utils/qualityChartUtils";
import QualityChartSection from "../components/QualityChartSection";
import QualityPieChartSection from "../components/QualityPieChartSection";
import QualityHeatMapSection from "../components/QualityHeatMapSection";
import QualityWasteQualitySection from "../components/QualityWasteQualitySection";
import { countMaterialBarItems } from "../../../components/charts/MaterialCountingHorizontalBarChart";

const StatisticsChartComponent = ({
  periodHistoryStart,
  periodHistoryStop,
  keywordFilter,
  handleValidate,
  setHandleValidate,
  chartCaptureRefs,
  sortingErrorCaptureFlux = null,
}) => {
  const { t } = useTranslation();
  const materials =
    JSON.parse(localStorage.getItem("materialsDescription")) || [];

  const {
    data,
    dataSortingError,
    monthlyCaracterisation,
    fluxesList,
    isLoading,
    tooManyReviews,
    totalAnomalies,
    dataVolume,
    selectedMaterial,
    setSelectedMaterial,
    selectedFlux,
    setSelectedFlux,
    selectedFluxSortingErrorPie,
    setSelectedFluxSortingErrorPie,
    selectedFluxCharacterizationPie,
    setSelectedFluxCharacterizationPie,
  } = useQualityStatisticsData(
    periodHistoryStart,
    periodHistoryStop,
    keywordFilter,
    handleValidate,
    setHandleValidate,
  );

  const {
    currentPage,
    setCurrentPage,
    totalPages,
    setTotalPages,
    weeksPageLabel,
    setWeeksPageLabel,
    weeksPerPage,
  } = useQualityChartSelections();

  useEffect(() => {
    setHandleValidate(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const safeData = useMemo(
    () => (data && typeof data === "object" ? data : {}),
    [data],
  );
  const safeDataSortingError = useMemo(
    () =>
      dataSortingError && typeof dataSortingError === "object"
        ? dataSortingError
        : {},
    [dataSortingError],
  );

  const { dataSetsSortingErrorByFluxChart, labelsByTimeChart } = useMemo(
    () => buildSortingErrorByFluxChartData(safeData, t),
    [safeData, t],
  );

  const { labels, values, colorSet, total } = extractFluxData(
    safeDataSortingError,
    selectedFluxSortingErrorPie,
    t,
  );

  const { labelsData, valuesData, colorSetData, materialTotal } =
    extractCharacterizationData(
      monthlyCaracterisation,
      selectedFluxCharacterizationPie,
      t,
    );

  const materialsList = materials
    .filter((material) => material.isActiveSortingError)
    .filter((material) => !material.referenceName.includes("hw_"))
    .map((material) => material.referenceName);

  const characterizationSlotCount = Math.max(
    countMaterialBarItems(values, labels),
    countMaterialBarItems(valuesData, labelsData),
  );

  return (
    <div>
      {!isLoading ? (
        tooManyReviews.numberOfReviews > 0 ? (
          <section className="statistics-card flex flex-col items-center justify-center gap-2 py-10 text-center text-[#B42318]">
            <span className="fas fa-exclamation-circle text-xl"></span>
            <span>
              {t("Too many reviews", {
                numberOfReviews: tooManyReviews.numberOfReviews,
                maxNumberOfReviews: tooManyReviews.maxNumberOfReviews,
              })}
            </span>
          </section>
        ) : Object.keys(safeData).length > 0 &&
          Object.keys(safeDataSortingError).length > 0 ? (
          <div className="flex flex-col gap-6">
            <section className="flex flex-col min-lg:flex-row gap-6">
              <QualityChartSection
                dataSetsSortingErrorByFluxChart={
                  dataSetsSortingErrorByFluxChart
                }
                labelsByTimeChart={labelsByTimeChart}
                t={t}
              />

              <article className="statistics-card min-lg:flex-1 min-lg:min-w-0">
                <div className="dashboard-chart-heading">
                  <div className="dashboard-chart-heading-text">
                    <h5 className="dashboard-chart-title">
                      {t(
                        "Trend of tracked objects in proportion to the overall flux",
                      )}
                    </h5>
                    <p className="dashboard-chart-subtitle">
                      {t("quality.trend.subtitle")}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <div className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#E5E5E5] text-sm whitespace-nowrap">
                    <span className="text-gray-500">{t("Flux")} :</span>
                    <select
                      value={selectedFlux}
                      onChange={(event) => setSelectedFlux(event.target.value)}
                      className="bg-transparent font-semibold text-[#000000] focus:outline-none cursor-pointer"
                    >
                      {fluxesList.map((flux) => (
                        <option key={flux.id} value={flux.id}>
                          {flux.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#E5E5E5] text-sm whitespace-nowrap">
                    <span className="text-gray-500">
                      {capitalizeFirstLetter(t("tracked object"))} :
                    </span>
                    <select
                      value={selectedMaterial}
                      onChange={(event) =>
                        setSelectedMaterial(event.target.value)
                      }
                      className="bg-transparent font-semibold text-[#000000] focus:outline-none cursor-pointer"
                    >
                      {materialsList.map((material) => (
                        <option key={material} value={material}>
                          {t(material)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <WasteHistogram
                  data={monthlyCaracterisation}
                  selectedMaterial={selectedMaterial}
                  selectedFlux={selectedFlux}
                />
              </article>
            </section>

            <section className="grid grid-cols-1 min-lg:grid-cols-2 gap-6 items-stretch">
              <QualityPieChartSection
                title={t(
                  "Sorting error characterization and objects tracked by waste category",
                )}
                subtitle={t("quality.sortingErrorPie.subtitle")}
                fluxesList={fluxesList}
                selectedFlux={selectedFluxSortingErrorPie}
                setSelectedFlux={setSelectedFluxSortingErrorPie}
                values={values}
                labels={labels}
                colorSet={colorSet}
                total={total}
                slotCount={characterizationSlotCount}
                t={t}
                forceSelectedFlux={sortingErrorCaptureFlux}
                chartPlotRef={(el) => {
                  if (chartCaptureRefs?.current) {
                    chartCaptureRefs.current.sortingErrors = el;
                  }
                }}
              />

              <QualityPieChartSection
                title={t(
                  "Characterization of objects tracked by waste category",
                )}
                subtitle={t("quality.characterizationPie.subtitle")}
                fluxesList={fluxesList}
                selectedFlux={selectedFluxCharacterizationPie}
                setSelectedFlux={setSelectedFluxCharacterizationPie}
                values={valuesData}
                labels={labelsData}
                colorSet={colorSetData}
                total={materialTotal}
                slotCount={characterizationSlotCount}
                t={t}
              />
            </section>

            <section className="flex flex-col min-lg:flex-row gap-6">
              <QualityHeatMapSection
                title={t("quality.heatmap.weekday.title")}
                subtitle={t("quality.heatmap.weekday.subtitle")}
                fixedViewMode="day"
                anomalies={totalAnomalies}
                t={t}
              />
              <QualityWasteQualitySection dataVolume={dataVolume} t={t} />
            </section>

            <QualityHeatMapSection
              title={t("sorting_errors")}
              subtitle={t("quality.heatmap.subtitle")}
              anomalies={totalAnomalies}
              currentPage={currentPage}
              weeksPerPage={weeksPerPage}
              setTotalPages={setTotalPages}
              setWeeksPageLabel={setWeeksPageLabel}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              weeksPageLabel={weeksPageLabel}
              t={t}
            />
          </div>
        ) : (
          <section className="statistics-card flex flex-col items-center justify-center gap-2 py-10 text-center text-[#5e5e5e]">
            <span className="fas fa-exclamation-triangle text-xl"></span>
            <span>{t("No anomalies for the current period")}</span>
          </section>
        )
      ) : (
        <div>
          <Loading height={"55vh"} />
        </div>
      )}
    </div>
  );
};

export default StatisticsChartComponent;
